import { forwardRef } from 'react';

import { ENCLOSURE_SHAPES } from '@frontend/api';
import { Badge, PopoverTrigger, type ButtonProps } from '@frontend/components';

import { FindingInlineItem } from './FindingInlineItem';
import { FindingLabel } from './FindingLabel';
import type { FindingData } from './types';

type BadgeProps = { count: number; isInPreview?: boolean };

const PillBadge = ({ count, isInPreview = false }: BadgeProps) => {
  return (
    <Badge
      display="inline-flex"
      position="relative"
      top="1px"
      transition="all 0.15s ease-in-out"
      {...(isInPreview
        ? {
            bg: 'transparent',
            borderColor: 'separator.divider',
            color: 'feature.secondary',
          }
        : {
            bg: 'page.plateMedium',
            borderColor: 'page.plateLight',
            color: 'feature.primary',
          })}
      sx={{
        'button:hover &': {
          bg: 'interaction.surfaceHover',
          borderColor: 'interaction.hover',
          color: 'interaction.hover',
        },
        'button:active &': {
          bg: 'interaction.surfaceActive',
          borderColor: 'interaction.active',
          color: 'interaction.active',
        },
        'button[data-active] &': {
          bg: 'interaction.surfaceActive',
          borderColor: 'interaction.active',
          color: 'interaction.active',
        },
      }}
    >
      {count}
    </Badge>
  );
};

const TextBadge = ({ count, isInPreview = false }: BadgeProps) => {
  return (
    <Badge
      display="inline-flex"
      position="relative"
      top="3px"
      transition="all 0.15s ease-in-out"
      {...(isInPreview
        ? {
            bg: 'transparent',
            borderColor: 'separator.divider',
            color: 'feature.secondary',
          }
        : {
            bg: 'page.plateMedium',
            borderColor: 'page.plateLight',
            color: 'feature.primary',
          })}
      sx={{
        'button:hover &': {
          bg: 'interaction.surfaceHover',
          borderColor: 'interaction.surfaceHover',
          color: 'interaction.hover',
        },
        'button:active &': {
          bg: 'interaction.surfaceActive',
          borderColor: 'interaction.surfaceActive',
        },
        'button[data-active] &': {
          bg: 'interaction.surfaceActive',
          borderColor: 'interaction.surfaceActive',
          color: 'interaction.active',
        },
      }}
    >
      {count}
    </Badge>
  );
};

export const FindingPopoverTrigger = forwardRef<
  HTMLButtonElement,
  {
    finding: FindingData;
    isOpen: boolean;
    isInPreview?: boolean;
    isHighlighted?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
  } & ButtonProps
>(
  (
    {
      finding,
      isOpen,
      isInPreview = false,
      isHighlighted = false,
      onOpen,
      onClose,
      ...rest
    },
    ref,
  ) => {
    const { variant: _variant, ...restButtonProps } = rest;
    const {
      label,
      displayType,
      count,
      isGdt,
      controlFrameProps,
      enclosureShape,
    } = finding;
    const isPill = displayType === 'pill';
    const size: 'sm' | 'md' = isPill ? 'sm' : 'md';

    return (
      <PopoverTrigger>
        <FindingInlineItem
          ref={ref}
          variant={displayType}
          isOpen={isOpen}
          isHighlighted={isHighlighted}
          // NB: Align items and padding top are only needed for GDTControlFrame and enclosure shapes,
          // and this is kind of a hack anyway because it violates our pill sizing approach,
          // but we don't have any alternatives for now.
          {...(controlFrameProps ||
          (enclosureShape && enclosureShape !== ENCLOSURE_SHAPES.PARENTHESES)
            ? { alignItems: 'center', paddingTop: '1px', paddingBottom: '1px' }
            : {})}
          {...restButtonProps}
        >
          {count > 1 ? (
            <>
              <FindingLabel
                label={label}
                isGdt={isGdt}
                controlFrameProps={controlFrameProps ?? undefined}
                enclosureShape={enclosureShape}
                size={size}
              />
              {isPill ? (
                <PillBadge count={count} isInPreview={isInPreview} />
              ) : (
                <TextBadge count={count} isInPreview={isInPreview} />
              )}
            </>
          ) : (
            <FindingLabel
              label={label}
              isGdt={isGdt}
              controlFrameProps={controlFrameProps ?? undefined}
              enclosureShape={enclosureShape}
              size={size}
            />
          )}
        </FindingInlineItem>
      </PopoverTrigger>
    );
  },
);
FindingPopoverTrigger.displayName = 'FindingPopoverTrigger';
